import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Col, Empty, List, Row, Space, Table, Tag, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import pmvService from '@/services/pmv.service';

const { Text } = Typography;

/**
 * Component Control Tower — /asset/pmv/components/control-tower
 */
const PmvComponentControlTowerPage: React.FC = () => {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['pmv-component-control-tower'],
    queryFn: () => pmvService.getComponentControlTower(),
    staleTime: 30_000,
  });
  const s = q.data;
  const h = s?.health;

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Component Control Tower"
        subtitle="Fleet component health & attention — evidence only, no fabricated cost KPIs"
        primaryActions={
          <Space>
            <Button onClick={() => navigate('/asset/pmv/components')}>Component Register</Button>
            <Button onClick={() => navigate('/asset/pmv/components/import')}>Import / diagnostics</Button>
            <Button onClick={() => navigate('/asset/pmv/control-tower')}>Fleet Command Center</Button>
          </Space>
        }
      />

      {s?.economics ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={s.economics.deferred ? 'Economics deferred' : 'Economics (evidence only)'}
          description={s.economics.note}
        />
      ) : null}

      {!q.isLoading && (h?.total_components ?? 0) === 0 ? (
        <Empty
          style={{ marginBottom: 16 }}
          description="No active components yet — open Component Register or Import"
        >
          <Button type="primary" onClick={() => navigate('/asset/pmv/components')}>
            Open register
          </Button>
        </Empty>
      ) : null}

      {s?.headlines?.length ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="Dominant signals"
          description={
            <List
              size="small"
              dataSource={s.headlines}
              renderItem={(line) => <List.Item style={{ padding: '4px 0' }}>{line}</List.Item>}
            />
          }
        />
      ) : null}

      <ControlTowerSectionCard zone="metricsOrders" title="Fleet component health">
        <ControlTowerMetricStrip zone="metricsOrders" title="Health">
          <Col xs={12} sm={8} md={6} lg={6}>
            <ControlTowerMetricTile title="Tyres installed" count={h?.tyres_installed ?? 0} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={6}>
            <ControlTowerMetricTile title="Tyres available" count={h?.tyres_available ?? 0} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={6}>
            <ControlTowerMetricTile
              title="Tyres attention"
              count={h?.tyres_requiring_attention ?? 0}
              urgency={(h?.tyres_requiring_attention ?? 0) > 0 ? 'attention' : 'neutral'}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={6}>
            <ControlTowerMetricTile
              title="Batteries attention"
              count={h?.batteries_requiring_attention ?? 0}
              urgency={(h?.batteries_requiring_attention ?? 0) > 0 ? 'attention' : 'neutral'}
            />
          </Col>
        </ControlTowerMetricStrip>
        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col xs={12} md={6}>
            <Text type="secondary">Batteries installed</Text>
            <div>{h?.batteries_installed ?? '—'}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Batteries available</Text>
            <div>{h?.batteries_available ?? '—'}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Under repair</Text>
            <div>{s?.lifecycle?.under_repair ?? '—'}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Under retread</Text>
            <div>{s?.lifecycle?.under_retread ?? '—'}</div>
          </Col>
        </Row>
      </ControlTowerSectionCard>

      <ControlTowerSectionCard zone="metricsOrders" title="Economics & performance" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col xs={12} md={6}>
            <Text type="secondary">Tyre median cost/km</Text>
            <div>
              {s?.economics?.summary?.unit_cost_benchmarks?.tyre?.insufficient
                ? `Insufficient sample (N=${s.economics.summary.unit_cost_benchmarks.tyre.n})`
                : s?.economics?.summary?.unit_cost_benchmarks?.tyre?.median_per_km ?? 'Unavailable'}
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Battery median cost/month</Text>
            <div>
              {s?.economics?.summary?.unit_cost_benchmarks?.battery?.insufficient
                ? `Insufficient sample (N=${s.economics.summary.unit_cost_benchmarks.battery.n})`
                : s?.economics?.summary?.unit_cost_benchmarks?.battery?.median_per_month ??
                  'Unavailable'}
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Warranties ≤30d</Text>
            <div>{s?.economics?.summary?.warranty?.expiring_30d ?? '—'}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Open claims</Text>
            <div>{s?.economics?.summary?.warranty?.open_claims ?? '—'}</div>
          </Col>
        </Row>
        {(s?.economics?.summary?.supplier_model_performance?.length ?? 0) > 0 ? (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">Supplier / model (min sample enforced)</Text>
            <Space wrap style={{ marginTop: 8 }}>
              {s!.economics!.summary!.supplier_model_performance!.slice(0, 8).map((r) => (
                <Tag key={r.code} color={r.ranking === 'INSUFFICIENT_SAMPLE' ? undefined : 'blue'}>
                  {r.code} · N={r.sample_n} · {r.ranking}
                </Tag>
              ))}
            </Space>
          </div>
        ) : null}
      </ControlTowerSectionCard>

      <ControlTowerSectionCard zone="attention" title="Dominant attention" style={{ marginTop: 16 }}>
        {(s?.dominant_attention?.length ?? 0) === 0 ? (
          <Empty description="No attention codes in current scan" />
        ) : (
          <Space wrap>
            {s!.dominant_attention.map((d) => (
              <Tag key={d.code} color="warning">
                {d.code.replace(/_/g, ' ')} · {d.count}
              </Tag>
            ))}
          </Space>
        )}
      </ControlTowerSectionCard>

      <ControlTowerSectionCard zone="attention" title="Attention queue" style={{ marginTop: 16 }}>
        <Table<{
          component_id: string;
          component_number: string;
          family: string;
          max_severity: string | null;
          fleet_number: string | null;
          position_code: string | null;
          labels: string[];
        }>
          rowKey="component_id"
          loading={q.isLoading}
          size="middle"
          pagination={{ pageSize: 20, responsive: true }}
          scroll={{ x: 720 }}
          dataSource={s?.attention_items ?? []}
          locale={{ emptyText: <Empty description="No components currently flagged" /> }}
          columns={[
            {
              title: 'Component',
              dataIndex: 'component_number',
              render: (v, r) => (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => navigate(`/asset/pmv/components/${r.component_id}`)}
                >
                  {v}
                </Button>
              ),
            },
            { title: 'Family', dataIndex: 'family', width: 100 },
            {
              title: 'Severity',
              dataIndex: 'max_severity',
              width: 110,
              render: (v) =>
                v ? (
                  <Tag color={v === 'CRITICAL' ? 'error' : v === 'WARNING' ? 'warning' : undefined}>
                    {v}
                  </Tag>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Equipment',
              key: 'eq',
              render: (_, r) =>
                [r.fleet_number, r.position_code].filter(Boolean).join(' · ') || '—',
            },
            {
              title: 'Findings',
              dataIndex: 'labels',
              render: (labels: string[]) =>
                (labels || []).length ? (
                  <Space size={4} wrap>
                    {labels.slice(0, 3).map((l) => (
                      <Tag key={l}>{l}</Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">None</Text>
                ),
            },
          ]}
        />
      </ControlTowerSectionCard>
    </WorkspaceLayout>
  );
};

export default PmvComponentControlTowerPage;
